import {useState, useEffect, useRef, useCallback} from 'react'

export const useInView = (options = {}) => {
    // Default to false for lazy loading use cases - component won't render until in view
    const {initialInView = false, triggerOnce = false} = options
    const [isInView, setIsInView] = useState(initialInView)
    const [hasBeenInView, setHasBeenInView] = useState(initialInView)
    const [entry, setEntry] = useState(null)
    // Use state for element to trigger re-render when ref is set
    const [element, setElement] = useState(null)
    const observerRef = useRef(null)

    // Internal helper to build thresholds
    const buildThresholdList = (numSteps) => {
        const thresholds = [0] // Start with 0

        for (let i = 1; i <= numSteps; i++) {
            thresholds.push(i / numSteps)
        }

        return thresholds
    }

    // Callback ref - using state ensures React re-renders when element changes
    const setRef = useCallback((node) => {
        // Only update if node actually changed
        setElement((prevNode) => {
            if (prevNode !== node) {
                return node
            }
            return prevNode
        })
    }, [])

    // Main observer effect
    useEffect(() => {
        // Only run on client
        if (typeof window === 'undefined' || !element) return

        // If triggerOnce and already been in view, don't re-observe
        if (triggerOnce && hasBeenInView) return

        // Extract custom options
        const {steps, initialInView: _, triggerOnce: __, ...observerOptions} = options

        // Build observer config
        const config = {
            rootMargin: '0px',
            ...observerOptions
        }

        // Only use threshold builder if steps is specified AND threshold is not already set
        if (steps !== undefined && !observerOptions.threshold) {
            config.threshold = buildThresholdList(steps)
        }

        // Clean up any existing observer
        if (observerRef.current) {
            observerRef.current.disconnect()
        }

        const handleIntersect = ([observerEntry]) => {
            const inView = observerEntry.isIntersecting
            setIsInView(inView)
            setEntry(observerEntry)

            if (inView) {
                setHasBeenInView(true)
                // Disconnect if triggerOnce is enabled
                if (triggerOnce && observerRef.current) {
                    observerRef.current.disconnect()
                }
            }
        }

        observerRef.current = new IntersectionObserver(handleIntersect, config)
        observerRef.current.observe(element)

        // Check initial visibility immediately after observing
        // This handles the case where the element is already in view when mounted
        const rect = element.getBoundingClientRect()
        const rootMarginPx = parseInt(config.rootMargin) || 0
        const isInitiallyVisible =
            rect.top < (window.innerHeight + rootMarginPx) &&
            rect.bottom > -rootMarginPx &&
            rect.left < (window.innerWidth + rootMarginPx) &&
            rect.right > -rootMarginPx

        if (isInitiallyVisible) {
            setIsInView(true)
            setHasBeenInView(true)
            if (triggerOnce && observerRef.current) {
                observerRef.current.disconnect()
            }
        }

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect()
                observerRef.current = null
            }
        }
    }, [element, options.rootMargin, options.threshold, options.root, options.steps, triggerOnce, hasBeenInView])

    return {
        ref: setRef,
        isInView,
        hasBeenInView, // Stays true once element has been in view (useful for lazy loading)
        entry
    }
}
